import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { flushSync } from "react-dom"; // Added for synchronous state updates

export const useOneTimeUserTable = ({
  oneTimeUsers,
  setOneTimeUsers,
  departments,
  setError,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  selectedDepartment,
  setSelectedDepartment,
  departmentId,
  setDepartmentId,
  editIndex,
  setEditIndex,
  adderror,
  setAddError,
  searchTerm,
  setSearchTerm,
  page,
  setPage,
  setOpen,
  setSuccessOpen,
  rowsPerPage = 10,
}) => {
  

  const handleAddEmployeeSubmit = (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim() || !selectedDepartment) {
      setAddError("Please fill in all required fields.");
      return;
    }
    const newEmail = email.trim().toLowerCase();
    const isDuplicate = oneTimeUsers.some(
      (user, idx) => user.email.toLowerCase() === newEmail && idx !== editIndex
    );
    if (isDuplicate) {
      setAddError(`User with email "${email}" already exists.`);
      return;
    }
    const newEmployee = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: newEmail,
      departmentId: departmentId,
      departmentName: selectedDepartment,
      password: password.trim(),
      assignmentId: 3,
      isActive: true,
    };
    if (editIndex !== null) {
      setOneTimeUsers((prev) => {
        const updated = [...prev];
        updated[editIndex] = newEmployee;
        return updated;
      });
    } else {
      setOneTimeUsers((prev) => [...prev, newEmployee]);
    }
    console.log("handleAddEmployeeSubmit: editIndex before successOpen:", editIndex);
    flushSync(() => {
      setSuccessOpen(true); // Force synchronous update
    });
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setShowPassword(false);
    setDepartmentId("");
    setSelectedDepartment("");
    setAddError("");
    setOpen(false);
    setEditIndex(null);
  };

  const handleDeleteEmployee = (email) => {
    setOneTimeUsers((prev) =>
      prev.map((user) => (user.email === email ? { ...user, isActive: false } : user))
    );
    console.log(`🗑️ Soft-deleted employee with email: ${email}`);
  };

  const handleUndoDelete = (email) => {
    setOneTimeUsers((prev) =>
      prev.map((user) => (user.email === email ? { ...user, isActive: true } : user))
    );
    console.log(`↩️ Undo delete for email: ${email}`);
  };

  const getCellValue = (cell) => {
    if (!cell) return "";

    const value = cell.value;

    if (typeof value === "object" && value !== null) {
      if (value.hyperlink) {
        // e.g. mailto:abc@test.com
        return value.text || value.hyperlink.replace("mailto:", "").trim();
      }
      if (value.richText) {
        return value.richText.map((t) => t.text).join("").trim();
      }
      if (value.text) {
        return value.text.toString().trim();
      }
      if (value.result) {
        return value.result.toString().trim();
      }
      return value.toString().trim();
    }

    return value?.toString().trim() || "";
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      const worksheet = workbook.worksheets[0];
      const rows = [];
      const maxRows = worksheet.rowCount;
      const chunkSize = 500;
      const processChunk = async (startRow) => {
        const endRow = Math.min(startRow + chunkSize - 1, maxRows);
        for (let rowNumber = startRow; rowNumber <= endRow; rowNumber++) {
          const row = worksheet.getRow(rowNumber);
          if (!row || row.cellCount === 0) continue;
          const firstName = row.getCell(1).value?.toString().trim() || "";
          const lastName = row.getCell(2).value?.toString().trim() || "";
          const email = getCellValue(row.getCell(3)).toLowerCase();
          const departmentName = row.getCell(4).value?.toString().trim() || "";
          const password = row.getCell(5).value?.toString().trim() || "";
          const departmentObj = departments.find(
            (d) => d.name.toLowerCase() === departmentName.toLowerCase()
          );
          if (!firstName || !lastName || !email || !password || !departmentObj) {
            console.warn(`Skipping invalid row at line ${rowNumber}`);
            continue;
          }
          rows.push({
            firstName,
            lastName,
            email,
            password,
            departmentId: departmentObj.id,
            departmentName: departmentObj.name,
            isActive: true,
            assignmentId: 3,
          });
        }
        if (endRow < maxRows) {
          await new Promise((resolve) => setTimeout(resolve, 0));
          return processChunk(endRow + 1);
        }
      };
      await processChunk(2);
      const existingEmails = new Set(oneTimeUsers.map((u) => u.email.toLowerCase()));
      const newUsers = [];
      for (const user of rows) {
        if (existingEmails.has(user.email)) {
          console.warn(`Duplicate email skipped: ${user.email}`);
          continue;
        }
        existingEmails.add(user.email);
        newUsers.push(user);
      }
      if (newUsers.length === 0) {
        alert("All users in Excel are duplicates or invalid.");
        return;
      }
      setOneTimeUsers((prev) => [...prev, ...newUsers]);
      alert("Excel data uploaded successfully!");
    } catch (error) {
      console.error("Error parsing Excel file:", error);
      setError?.("Failed to parse Excel file. Please ensure it is a valid .xlsx file with correct columns.");
    }
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("OneTimeUsers");
    worksheet.columns = [
      { header: "First Name", key: "firstName", width: 20 },
      { header: "Last Name", key: "lastName", width: 20 },
      { header: "Email", key: "email", width: 30 },
      { header: "Departments", key: "departments", width: 30 },
      { header: "Password", key: "password", width: 20 },
    ];
    worksheet.addRow({
      firstName: "abc",
      lastName: "def",
      email: "abc@gmail.com",
      departments: departments[0]?.name,
      password: "1234",
    });
    const departmentList = departments.map((d) => d.name).join(",");
    for (let row = 2; row <= 250; row++) {
      worksheet.getCell(`D${row}`).dataValidation = {
        type: "list",
        allowBlank: false,
        formulae: [`"${departmentList}"`],
        showErrorMessage: true,
        errorStyle: "error",
        errorTitle: "Invalid Department",
        error: "Please select a valid department from the list.",
      };
    }
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "OneTimeUserTemplate.xlsx");
  };

  const filteredUsers = oneTimeUsers.filter((user) => {
    const searchFields = [
      user.firstName,
      user.lastName,
      user.email,
      user.departmentName || "",
    ].join(" ").toLowerCase();
    return searchFields.includes(searchTerm.toLowerCase());
  });

  console.log(filteredUsers, "filtered users");

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const currentRows = filteredUsers.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }
    return pages;
  };

  const handleEditClick = (index) => {
    const user = oneTimeUsers[index];
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setPassword(user.password);
    setDepartmentId(user.departmentId?.toString() || "");
    setSelectedDepartment(user.departmentName);
    setEditIndex(index);
    setOpen(true);
  };

  return {
    handleAddEmployeeSubmit,
    handleDeleteEmployee,
    handleUndoDelete,
    handleExcelUpload,
    handleDownloadTemplate,
    filteredUsers,
    totalPages,
    currentRows,
    handlePageChange,
    renderPageNumbers,
    handleEditClick,
    rowsPerPage,
  };
};
